import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames/bind';
import find from 'lodash/find';
import map from 'lodash/map';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import get from 'lodash/get';
import currencyFormatter from 'currency-formatter';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import IconGift from 'yoda-core-components/lib/assets/svg/IconGift.svg';
import S7Image from 'yoda-core-components/lib/components/S7Image/S7Image';
import DeliveryMsg from 'yoda-checkout-components/lib/components/DeliveryMsg/DeliveryMsg';
import WarningMessage from 'yoda-checkout-components/lib/components/WarningMessage/WarningMessage';
import ProductAttributes from 'yoda-checkout-components/lib/components/ProductAttributes/ProductAttributes';
import { getOrderShippingZipcode } from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import { isExtensionRequired } from 'yoda-checkout-components/lib/helpers/Utils/ATCCommonUtils';
import ChildItemsGroup from 'yoda-checkout-components/lib/components/ChildItemsGroup/ChildItemsGroup';
import ProductPriceStack from 'yoda-checkout-components/lib/components/ProductPriceStack/ProductPriceStack';
import {
    SHIPPING_GROUP_TYPE_BOPIS,
    FEE_TYPES_RECYCLE,
    EXPOSED_PACKAGING_CHECKOUT_MSG,
    itemSurchargeTypes,
} from 'yoda-checkout-components/lib/common/Constants';
import { GIFT_REGISTRY } from 'yoda-checkout-components/lib/common/LabelConstants';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import * as styles from './ProductDetails.css';
import { isSilverProduct } from '../../helpers/ProductTypeUtils/ProductTypeUtils';
import { getProductPPIds } from '../../helpers/Utils/Utils';

const cx = classNames.bind(styles);
function ProductDetails(props) {
    const {
        orderItem,
        orderAdjustments,
        shippingType,
        orderGiftRegistries,
        currency,
        enableExposedPackagingMessage,
        shippingGroupDetails,
        enableSuppressDeliveryDates,
        syndicateBrands,
        orderShippingGroupsInfo,
        orderShippingType,
        enableSingleDeliveryDateSTH,
        enableMarketingLabel,
    } = props;

    const {
        product: { imageURL, name, type, modelNumber, brandName, subdivision, shipAlone },
        quantity,
        sku: { attributes },
        deliveryOptions,
    } = orderItem;
    const isTruckable = orderItem.product.truckable;
    const deliveryInfo = find(deliveryOptions, { selected: true });
    const estDeliveryMsg = (deliveryInfo && deliveryInfo.deliveryPromiseMessage) || '';
    const itemZipcode = getOrderShippingZipcode(orderShippingGroupsInfo);
    const isShowExposedPackagingMessage =
        shippingType !== SHIPPING_GROUP_TYPE_BOPIS && shipAlone && enableExposedPackagingMessage;
    let isGiftRegistry = false;
    let giftRegistryItem = null;
    if (orderGiftRegistries && !isEmpty(orderGiftRegistries.items)) {
        giftRegistryItem = find(orderGiftRegistries.items, ['itemId', orderItem.id]);
        if (giftRegistryItem && giftRegistryItem.regId) {
            isGiftRegistry = true;
        }
    }
    const styleConfig = {
        customStyle: {
            protectionPlanDescription: cx('protectionPlanDescription'),
        },
    };

    function renderRecycleFee() {
        const recycleFeeItem = find(orderItem.childItems, { type: FEE_TYPES_RECYCLE });
        if (recycleFeeItem) {
            const feeName = recycleFeeItem.product.name;
            const itemamount = find(recycleFeeItem.totals, { name: 'ITEM AMOUNT' });
            if (itemamount && itemamount.amount > 0) {
                return (
                    <div
                        className={dt(['text-small', 'text-right'])}
                        data-automation-id="at-product-recycle-fee"
                    >
                        <strong
                            data-automation-id="at-product-recycle-fee-price"
                            className={cx('childProductPrice')}
                        >
                            {currencyFormatter.format(itemamount.amount, { code: currency })}
                        </strong>
                        <span
                            data-automation-id="at-product-recycle-fee-label"
                            className={dt(['mt-1'])}
                        >
                            &nbsp;{feeName || 'Recycle Fee'}
                        </span>
                    </div>
                );
            }
        }

        return '';
    }

    function renderSurCharges() {
        return map(orderItem.charges, (charge) => {
            if (charge.amount && charge.amount > 0) {
                return (
                    <div className={dt(['text-small', 'text-right'])}>
                        <span data-automation-id="at-surcharge-price">
                            {currencyFormatter.format(charge.amount, { code: currency })}
                        </span>
                        <span data-automation-id="at-surcharge-label">
                            &nbsp;{itemSurchargeTypes[`${charge.code}`].description}
                        </span>
                    </div>
                );
            }
            return '';
        });
    }

    function renderProductTitle(productName, productBrandName) {
        const isSyndicateBrands = includes(syndicateBrands, productBrandName);
        if (isSyndicateBrands) {
            return <span dangerouslySetInnerHTML={{ __html: productName }} />; // eslint-disable-line
        }
        return productName;
    }

    const isCarePlanEligible = get(orderItem, 'product.carePlanEligible', false);

    return (
        <>
            <div
                data-automation-id="at-product-detail"
                data-ppId={getProductPPIds([orderItem])}
                className={dt(['text-left', 'flex', 'justify-between', 'w-full'])}
            >
                <div data-automation-id="at-product-image" className={dt(['align-top', 'w-2/5'])}>
                    <div>
                        <S7Image
                            src={imageURL}
                            alt={name}
                            automationId="view-at-product-image"
                            extensionRequired={isExtensionRequired(subdivision)}
                        />
                    </div>
                </div>
                <div data-automation-id="at-product-price" className={dt(['inline-block'])}>
                    <ProductPriceStack
                        orderItem={orderItem}
                        orderAdjustments={orderAdjustments}
                        currency={currency}
                        quantity={orderItem.quantity}
                        dividerTheme={styles.priceDivider}
                        checkoutRender
                        onPriceDetailsClick={noop}
                        showPriceDetailsLink
                        showPriceDetailsBreakup
                        enableMarketingLabel={enableMarketingLabel}
                    />
                    {renderRecycleFee()}
                    {renderSurCharges()}
                </div>
            </div>
            <div
                data-automation-id="at-product-info"
                className={`${cx('productInfoBlock')} ${dt(['mt-6'])}`}
            >
                {isGiftRegistry && (
                    <div data-automation-id="at-gift-registry">
                        <Icon iconType="svg" width="40px" height="40px" viewBox="0 0 48 48">
                            <IconGift />
                        </Icon>
                        <span data-automation-id="at-gift-item" className={dt(['inline-block'])}>
                            {isEmpty(giftRegistryItem.regName)
                                ? 'Gift Item'
                                : GIFT_REGISTRY.FOR + giftRegistryItem.regName}
                        </span>
                    </div>
                )}
                <div
                    data-automation-id="ocProductTitle"
                    className={dt(['text-medium', 'font-open-sans-semibold'])}
                >
                    {renderProductTitle(name, brandName)}
                </div>
                <div className={dt(['mt-1', 'capitalize'])}>
                    {isSilverProduct(type) ? (
                        <div className={dt(['text-gray-70'])} data-automation-id="at-model-number">
                            Model #{modelNumber}
                        </div>
                    ) : (
                        ''
                    )}
                    <ProductAttributes attributes={attributes} />
                    <div
                        className={dt([
                            'text-gray-70',
                            'smMd:text-normal',
                            'lg:text-small',
                            'mt-1',
                        ])}
                        data-automation-id="at-product-quantity"
                    >
                        Qty: {quantity}
                    </div>
                </div>
                {shippingType !== SHIPPING_GROUP_TYPE_BOPIS &&
                !(
                    enableSuppressDeliveryDates &&
                    isEmpty(shippingGroupDetails && shippingGroupDetails.address)
                ) ? (
                    <div className={dt(['mt-1', 'text-small'])}>
                        <DeliveryMsg
                            isCheckoutPage
                            estDeliveryMsg={estDeliveryMsg}
                            theme={`${cx('deliveryMsgTheme')} ${dt([
                                'text-small',
                                'font-open-sans-bold',
                            ])}`}
                            isTruckable={isTruckable}
                            enableSingleDeliveryDateSTH={enableSingleDeliveryDateSTH}
                            itemZipcode={itemZipcode}
                            shippingType={orderShippingType}
                        />
                    </div>
                ) : (
                    ''
                )}
                {isShowExposedPackagingMessage && (
                    <WarningMessage
                        isCheckout
                        messageText={EXPOSED_PACKAGING_CHECKOUT_MSG}
                        automationId="at-exposed-packaging-block"
                    />
                )}
            </div>
            <div
                className={dt(['rounded-small'])}
                data-automation-id="at-product-detail-child-item-group-wrap"
            >
                <ChildItemsGroup
                    orderItem={orderItem}
                    hideCaliforniaContent
                    config={styleConfig}
                    isCarePlanEligible={isCarePlanEligible}
                />
            </div>
        </>
    );
}

ProductDetails.defaultProps = {
    currency: 'USD',
    enableSuppressDeliveryDates: false,
    enableExposedPackagingMessage: true,
    syndicateBrands: [],
    orderShippingGroupsInfo: [],
    orderShippingType: '',
    enableSingleDeliveryDateSTH: true,
    enableMarketingLabel: false,
};

ProductDetails.propTypes = {
    orderItem: PropTypes.objectOf(() => {}).isRequired,
    orderAdjustments: PropTypes.arrayOf(() => {}).isRequired,
    shippingType: PropTypes.string.isRequired,
    orderGiftRegistries: PropTypes.objectOf(PropTypes.object).isRequired,
    currency: PropTypes.string,
    shippingGroupDetails: PropTypes.objectOf(PropTypes.object).isRequired,
    enableSuppressDeliveryDates: PropTypes.bool,
    enableExposedPackagingMessage: PropTypes.bool,
    syndicateBrands: PropTypes.arrayOf(PropTypes.object),
    orderShippingGroupsInfo: PropTypes.arrayOf(PropTypes.object),
    orderShippingType: PropTypes.string,
    enableSingleDeliveryDateSTH: PropTypes.bool,
    enableMarketingLabel: PropTypes.bool,
};

const mapStateToProps = (state) => ({
    enableSuppressDeliveryDates: get(state, 'context.featureFlags.enableSuppressDeliveryDates'),
    enableExposedPackagingMessage: get(
        state,
        'context.featureFlags.enableExposedPackagingMessage',
        true
    ),
    syndicateBrands: ((state.context || {}).preferences || {}).syndicateBrands,
    orderShippingGroupsInfo: get(state, 'orderShippingGroupsInfo', []),
    orderShippingType: (state.orderInfo || {}).orderShippingType || '',
    enableSingleDeliveryDateSTH: get(
        state,
        'context.featureFlags.enableSingleDeliveryDateSTH',
        true
    ),
    enableMarketingLabel: get(state, 'context.featureFlags.enableMarketingLabel'),
});

export default connect(mapStateToProps, null)(ProductDetails);
